import { useState, useEffect } from 'react';
import { Activity, Archive, Zap } from 'lucide-react';
import { Spinner, LoadingState } from '@systemdox/ui';
import { stalenessApi, type StalenessReportItem, type UnarchivedChange } from '@/services/stalenessApi';
import { useProject } from '@/contexts/ProjectContext';
import { StalenessBadge } from '@/components/docs/StalenessBadge';
import { DocTypeBadge } from '@/components/docs/DocTypeBadge';

export default function StalenessReportPage() {
  const { selected: selectedProject } = useProject();
  const [report, setReport] = useState<StalenessReportItem[]>([]);
  const [unarchived, setUnarchived] = useState<UnarchivedChange[]>([]);
  const [thresholdDays, setThresholdDays] = useState(14);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    // Scoped to the selected project when one is chosen (view filter, task 11).
    stalenessApi
      .getReport(undefined, selectedProject?.projectId)
      .then(setReport)
      .finally(() => setLoading(false));
    stalenessApi
      .getUnarchivedChanges()
      .then(({ changes, thresholdDays: days }) => {
        setUnarchived(changes);
        setThresholdDays(days);
      })
      .catch(() => setUnarchived([]));
  }, [selectedProject?.projectId]);

  async function handleAnalyze(docId: string) {
    setAnalyzing(docId);
    try {
      const result = await stalenessApi.analyzeStaleness(docId);
      setReport((prev) =>
        prev.map((item) =>
          item.doc_id === docId
            ? { ...item, staleness_score: result.staleness_score ?? null, summary: result.summary || '' }
            : item
        )
      );
    } finally {
      setAnalyzing(null);
    }
  }

  if (loading) {
    return <LoadingState size="lg" />;
  }

  const analyzed = report.filter((r) => r.staleness_score !== null);
  const avgScore = analyzed.length > 0
    ? Math.round(analyzed.reduce((sum, r) => sum + (r.staleness_score || 0), 0) / analyzed.length)
    : null;

  return (
    <div className="p-8">
      {unarchived.length > 0 && (
        <div className="mb-6 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Archive className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Finished changes that never graduated
            </h2>
          </div>
          <p className="text-xs text-slate-600 dark:text-slate-400 mb-3">
            These change folders have their tasks done but still sit in{' '}
            <code className="px-1 rounded bg-amber-100 dark:bg-amber-900/40">specs/changes/</code>.
            Fold their deltas into the living docs and move them to{' '}
            <code className="px-1 rounded bg-amber-100 dark:bg-amber-900/40">specs/archive/</code>{' '}
            — after {thresholdDays} days without a commit they count as staleness defects
            (Documentation Standard §3.3).
          </p>
          <ul className="space-y-1.5">
            {unarchived.map((c) => (
              <li key={`${c.repo}/${c.path}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <a
                  href={`https://github.com/${c.repo}/tree/HEAD/${c.path.replace(/\/tasks\.md$/, '')}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono font-medium text-blue-700 dark:text-blue-400 hover:underline"
                >
                  {c.slug}
                </a>
                <span className="text-slate-500">{c.repo}</span>
                <span className="text-slate-500">{c.taskDone}/{c.taskTotal} tasks done</span>
                {c.daysSinceLastCommit !== null && (
                  <span className={c.stale ? 'text-amber-700 dark:text-amber-400 font-medium' : 'text-slate-500'}>
                    {c.daysSinceLastCommit}d since last commit{c.stale ? ' — stale' : ''}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Staleness Report</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            AI-powered documentation freshness analysis.
            {avgScore !== null && ` Average score: ${avgScore}/100.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-slate-400" />
          <span className="text-sm text-slate-500">{analyzed.length}/{report.length} analyzed</span>
        </div>
      </div>

      <div className="space-y-2">
        {report.map((item) => (
          <div
            key={item.doc_id}
            className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{item.title}</span>
                <DocTypeBadge type={item.doc_type} />
              </div>
              <p className="text-xs text-slate-500 truncate">{item.repo}/{item.path}</p>
              {item.summary && (
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">{item.summary}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <StalenessBadge score={item.staleness_score} />
              {item.staleness_score === null && (
                <button
                  onClick={() => handleAnalyze(item.doc_id)}
                  disabled={analyzing === item.doc_id}
                  className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50"
                >
                  {analyzing === item.doc_id ? (
                    <Spinner size="sm" tone="current" />
                  ) : (
                    <Zap className="w-3 h-3" />
                  )}
                  Analyze
                </button>
              )}
            </div>
          </div>
        ))}
        {report.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-12">No documents to analyze. Sync some repositories first.</p>
        )}
      </div>
    </div>
  );
}
